import { defineStore } from 'pinia'
import type { PermissionCode, ProjectGroup, RbacUser, RoleDefinition } from '../types'

const demoProjects: ProjectGroup[] = [
  { id: 'project-alpha', name: 'Project Alpha' },
  { id: 'project-beta', name: 'Project Beta' },
]

const demoRoles: RoleDefinition[] = [
  {
    id: 'alpha-admin',
    name: 'Project Admin',
    projectId: 'project-alpha',
    permissions: [
      'dashboard.view',
      'security.manage',
      'permission.view',
      'user.view',
      'project.view',
      'project.assign',
      'role.assign',
      'secret.view',
      'secret.create',
      'secret.toggle',
      'support.chat.view',
      'support.ticket.view',
      'support.settings.manage',
      'audit.view',
    ],
  },
  {
    id: 'alpha-developer',
    name: 'Developer',
    projectId: 'project-alpha',
    permissions: ['dashboard.view', 'security.manage', 'project.view', 'secret.view'],
  },
  {
    id: 'beta-auditor',
    name: 'Auditor',
    projectId: 'project-beta',
    permissions: ['dashboard.view', 'security.manage', 'audit.view', 'secret.view'],
  },
]

const demoUsers: RbacUser[] = [
  {
    id: 'user-1',
    name: 'Nate Scott',
    email: 'nate.scott@repodepot.local',
    memberships: [
      { projectId: 'project-alpha', roleIds: ['alpha-admin'] },
      { projectId: 'project-beta', roleIds: ['beta-auditor'] },
    ],
  },
  {
    id: 'user-2',
    name: 'Ari Chen',
    email: 'ari.chen@repodepot.local',
    memberships: [{ projectId: 'project-alpha', roleIds: ['alpha-developer'] }],
  },
]

export const usePermissionStore = defineStore('permission', {
  state: () => ({
    currentProjectId: 'project-alpha',
    currentUserId: 'user-1',
    projects: demoProjects as ProjectGroup[],
    users: demoUsers as RbacUser[],
    roles: demoRoles as RoleDefinition[],
    userPermissions: [] as PermissionCode[],
  }),
  getters: {
    currentUser(state): RbacUser | undefined {
      return state.users.find((user) => user.id === state.currentUserId)
    },
  },
  actions: {
    initialize() {
      this.rebuildUserPermissions()
    },
    switchProject(projectId: string) {
      if (this.currentProjectId === projectId) {
        return
      }
      this.currentProjectId = projectId
      this.rebuildUserPermissions()
    },
    setUserProjectRoles(userId: string, projectId: string, roleIds: string[]) {
      const user = this.users.find((item) => item.id === userId)
      if (!user) {
        return
      }

      const membership = user.memberships.find((item) => item.projectId === projectId)
      if (membership) {
        membership.roleIds = [...roleIds]
      } else {
        user.memberships.push({ projectId, roleIds: [...roleIds] })
      }

      this.rebuildUserPermissions()
    },
    setUserProjects(userId: string, projectIds: string[]) {
      const user = this.users.find((item) => item.id === userId)
      if (!user) {
        return
      }

      user.memberships = user.memberships.filter((membership) => projectIds.includes(membership.projectId))

      for (const projectId of projectIds) {
        const existing = user.memberships.find((membership) => membership.projectId === projectId)
        if (!existing) {
          const fallbackRole = this.roles.find((role) => role.projectId === projectId)
          user.memberships.push({ projectId, roleIds: fallbackRole ? [fallbackRole.id] : [] })
        }
      }

      this.rebuildUserPermissions()
    },
    rebuildUserPermissions() {
      const user = this.users.find((item) => item.id === this.currentUserId)
      if (!user) {
        this.userPermissions = ['dashboard.view']
        return
      }

      const membership = user.memberships.find((item) => item.projectId === this.currentProjectId)
      const permissionSet = new Set<PermissionCode>()

      // If the selected project doesn't exist in local demo memberships,
      // keep permissions from all memberships so app navigation remains usable.
      const membershipsToApply = membership ? [membership] : user.memberships

      for (const item of membershipsToApply) {
        for (const roleId of item.roleIds) {
          const role = this.roles.find((entry) => entry.id === roleId)
          if (!role) {
            continue
          }
          for (const permission of role.permissions) {
            permissionSet.add(permission)
          }
        }
      }

      if (permissionSet.size === 0) {
        permissionSet.add('dashboard.view')
      }

      this.userPermissions = Array.from(permissionSet)
    },
  },
})
